import React, { useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useNavigate } from 'react-router-dom';
import { logout, sso_logout } from '../services';
import { isSsoSession } from './ssoAuth/loginSession';
import { getUserFlags } from '../helper';

// Custom toggle component
const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <a
        href=""
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            onClick(e);
        }}
        className="dropdown-toggle"
    >
        {children}
    </a>
));

// Custom menu component with optional search filter
const CustomMenu = React.forwardRef(
    ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
        const [value, setValue] = useState('');

        return (
            <div
                ref={ref}
                style={style}
                className={className}
                aria-labelledby={labeledBy}
            >
                <ul className="list-unstyled">
                    {React.Children.toArray(children).filter(
                        (child) =>
                            !value ||
                            child.props.children
                                .toString()
                                .toLowerCase()
                                .startsWith(value.toLowerCase())
                    )}
                </ul>
            </div>
        );
    }
);

// Main component
const Logout = () => {
    const navigate = useNavigate();
    const userName = localStorage.getItem('email') || 'User'  // e.g., "Rashmi"
    // Role comes from user_flags, which is written on BOTH password and SSO
    // login (see loginSession.js). localStorage 'orgRole' was only set on the
    // password path, so it was empty/stale for SSO users.
    const orgRole = getUserFlags()?.role_slug   // 'org_admin' for org admins
    const firstLetter = userName ? userName.charAt(0).toUpperCase() : '';
    /**
     * Sign out of the identity provider as well as out of here.
     *
     * Without this the provider still considers the browser signed in, so the
     * next "Sign in with SSO" needs no re-authentication -- which on a shared
     * machine looks like the session was never ended.
     *
     * Every step is best-effort: the platform session is gone either way, and a
     * provider that cannot be reached must not leave the user staring at a
     * dashboard they have already logged out of.
     */
    const handleSsoLogout = async (refresh_token) => {
        // Best-effort: tell the provider to end its session server-side. We do
        // not hand the browser off to the provider's logout URL, because that
        // lands the user on Entra/Microsoft's own "You signed out" page unless
        // a post_logout_redirect_uri is registered in the provider app. Keeping
        // the redirect in-app instead means no provider config is required.
        try {
            await sso_logout({});
        } catch (err) {
            console.log("sso logout err", err)
        }

        // Still call the ordinary endpoint so the refresh token is blacklisted
        // exactly as it is for a password logout.
        try {
            await logout({ refresh_token });
        } catch (err) {
            console.log("logout err", err)
        }

        sessionStorage.clear();
        navigate('/')
    };

    const handleLogout = async () => {
        const refresh_token = sessionStorage.getItem('refresh_token')

        // Single Logout applies only to sessions that began at a provider.
        // Password sessions take exactly the path they always have.
        if (isSsoSession()) {
            await handleSsoLogout(refresh_token);
            return;
        }

        try {
            const params = {
                refresh_token: refresh_token
            }
            const response = await logout(params);
            console.log("response logout", response)
            const status = response.statusCode?.response_data?.status_code;

            if (status === 200) {
                sessionStorage.clear();
                navigate('/')
            }
        } catch (err) {
            console.log("err", err)
        }
    };


    return (
        <Dropdown className="cstm_dropdown">
            <Dropdown.Toggle className='font_500' as={CustomToggle} id="dropdown-custom-components">
                {firstLetter ? firstLetter :
                    <img src="https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/user-profile.svg" alt="user-profile" width="50" height="50" loading='lazy' />}
            </Dropdown.Toggle>

            <Dropdown.Menu as={CustomMenu}>
                <Dropdown.Item eventKey="1">
                    <div className="d-flex font_500 gap-3 align-items-center color_white">
                        <span className='user_name'>
                            {firstLetter}
                        </span>
                        <div className='d-flex font_500 flex-column'>
                            Username
                            <p className='mb-0 user_email'>
                                {userName}
                            </p>
                        </div>
                    </div>
                </Dropdown.Item>
                {/* <Dropdown.Item eventKey="2">
                    <div className="d-flex font_500 gap-4 color_white">
                        <svg width="20" height="20" viewBox="0 0 21 21" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M17.5 2.62521V6.12521M19.25 4.37521H15.75M3.5 14.8752V16.6252M4.375 15.7502H2.625M8.69482 13.5627C8.6167 13.2599 8.45887 12.9836 8.23774 12.7624C8.01661 12.5413 7.74026 12.3835 7.43745 12.3053L2.06932 10.9211C1.97774 10.8951 1.89713 10.8399 1.83973 10.764C1.78234 10.688 1.75128 10.5954 1.75128 10.5002C1.75128 10.405 1.78234 10.3124 1.83973 10.2365C1.89713 10.1605 1.97774 10.1053 2.06932 10.0793L7.43745 8.69422C7.74015 8.61617 8.01643 8.45847 8.23755 8.2375C8.45867 8.01653 8.61657 7.74037 8.69482 7.43772L10.0791 2.06959C10.1048 1.97765 10.1599 1.89664 10.236 1.83894C10.312 1.78123 10.4049 1.75 10.5004 1.75C10.5959 1.75 10.6887 1.78123 10.7648 1.83894C10.8409 1.89664 10.896 1.97765 10.9217 2.06959L12.3051 7.43772C12.3832 7.74053 12.541 8.01688 12.7622 8.23801C12.9833 8.45914 13.2596 8.61698 13.5624 8.69509L18.9306 10.0785C19.0229 10.1039 19.1043 10.159 19.1623 10.2352C19.2203 10.3113 19.2517 10.4045 19.2517 10.5002C19.2517 10.596 19.2203 10.6891 19.1623 10.7653C19.1043 10.8415 19.0229 10.8965 18.9306 10.922L13.5624 12.3053C13.2596 12.3835 12.9833 12.5413 12.7622 12.7624C12.541 12.9836 12.3832 13.2599 12.3051 13.5627L10.9208 18.9308C10.8951 19.0228 10.84 19.1038 10.7639 19.1615C10.6878 19.2192 10.595 19.2504 10.4995 19.2504C10.404 19.2504 10.3112 19.2192 10.2351 19.1615C10.159 19.1038 10.1039 19.0228 10.0782 18.9308L8.69482 13.5627Z" />
                        </svg>
                        Upgrade Now!
                    </div>
                </Dropdown.Item> */}
                <Dropdown.Item eventKey="3" onClick={() => navigate('/settings')}>
                    <div className="d-flex font_500 gap-4 color_white">
                        <svg width="20" height="20" viewBox="0 0 21 21" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M6.125 18.0793V16.625C6.125 16.1609 6.30937 15.7158 6.63756 15.3876C6.96575 15.0594 7.41087 14.875 7.875 14.875H13.125C13.5891 14.875 14.0342 15.0594 14.3624 15.3876C14.6906 15.7158 14.875 16.1609 14.875 16.625V18.0793M19.25 10.5C19.25 15.3325 15.3325 19.25 10.5 19.25C5.66751 19.25 1.75 15.3325 1.75 10.5C1.75 5.66751 5.66751 1.75 10.5 1.75C15.3325 1.75 19.25 5.66751 19.25 10.5ZM13.125 8.75C13.125 10.1997 11.9497 11.375 10.5 11.375C9.05025 11.375 7.875 10.1997 7.875 8.75C7.875 7.30025 9.05025 6.125 10.5 6.125C11.9497 6.125 13.125 7.30025 13.125 8.75Z" />
                        </svg>
                        Profile
                    </div>
                </Dropdown.Item>
                <Dropdown.Item eventKey="4" onClick={() => navigate('/settings')}>
                    <div className="d-flex font_500 gap-4 color_white">
                        <svg width="20" height="20" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M11 18.3335C12.9449 18.3335 14.8102 17.5609 16.1854 16.1856C17.5607 14.8103 18.3333 12.9451 18.3333 11.0002C18.3333 9.05524 17.5607 7.18998 16.1854 5.81471C14.8102 4.43945 12.9449 3.66683 11 3.66683M11 18.3335C9.05506 18.3335 7.1898 17.5609 5.81453 16.1856C4.43926 14.8103 3.66665 12.9451 3.66665 11.0002M11 18.3335V20.1668M11 3.66683C9.05506 3.66683 7.1898 4.43945 5.81453 5.81471C4.43926 7.18998 3.66665 9.05524 3.66665 11.0002M11 3.66683V1.8335M3.66665 11.0002H1.83331M12.8333 11.0002C12.8333 11.4864 12.6402 11.9527 12.2963 12.2965C11.9525 12.6403 11.4862 12.8335 11 12.8335C10.5137 12.8335 10.0474 12.6403 9.70362 12.2965C9.3598 11.9527 9.16665 11.4864 9.16665 11.0002C9.16665 10.5139 9.3598 10.0476 9.70362 9.7038C10.0474 9.35998 10.5137 9.16683 11 9.16683C11.4862 9.16683 11.9525 9.35998 12.2963 9.7038C12.6402 10.0476 12.8333 10.5139 12.8333 11.0002ZM12.8333 11.0002H20.1666M15.5833 18.9384L14.6666 17.3526M10.0833 9.41441L6.41665 3.06191M18.9384 15.5835L17.3525 14.6668M3.06161 6.41683L4.64745 7.3335M18.9384 6.41683L17.3525 7.3335M3.06161 15.5835L4.64745 14.6668M15.5833 3.06191L14.6666 4.64774M10.0833 12.586L6.41665 18.9385" />
                        </svg>
                        Settings
                    </div>
                </Dropdown.Item>
                {orgRole === "org_admin" && (<Dropdown.Item eventKey="5" onClick={() => navigate('/ssoAuth')}>
                    <div className="d-flex font_500 gap-4 color_white">
                        <svg width="20" height="20" viewBox="0 0 21 21" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M13.125 8.75V6.125C13.125 5.42881 12.8484 4.76113 12.3562 4.26884C11.8639 3.77656 11.1962 3.5 10.5 3.5C9.80381 3.5 9.13613 3.77656 8.64384 4.26884C8.15156 4.76113 7.875 5.42881 7.875 6.125V8.75M6.125 8.75H14.875C15.8415 8.75 16.625 9.5335 16.625 10.5V15.75C16.625 16.7165 15.8415 17.5 14.875 17.5H6.125C5.1585 17.5 4.375 16.7165 4.375 15.75V10.5C4.375 9.5335 5.1585 8.75 6.125 8.75Z" />
                        </svg>
                        SSO Authentication
                    </div>
                </Dropdown.Item>)}
                <Dropdown.Item eventKey="6" onClick={handleLogout}>
                    <div className="d-flex font_500 gap-4 color_white">
                        <svg width="20" height="20" viewBox="0 0 21 21" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M7.875 18.375H4.375C3.91087 18.375 3.46575 18.1906 3.13756 17.8624C2.80937 17.5342 2.625 17.0891 2.625 16.625V4.375C2.625 3.91087 2.80937 3.46575 3.13756 3.13756C3.46575 2.80937 3.91087 2.625 4.375 2.625H7.875M14 14.875L18.375 10.5M18.375 10.5L14 6.125M18.375 10.5H7.875" />
                        </svg>
                        Logout
                    </div>
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default Logout;
